import React, { useState } from 'react';
import { getRegionData } from '../utils/regionData';
import { CryptoPayment } from './CryptoPayment';
import { PaymentTimer } from './PaymentTimer';
import { Copy, Check } from 'lucide-react';
interface PaymentMethodsProps {
  selectedRegion: string;
  onPaymentSuccess: () => void;
}
export function PaymentMethods({
  selectedRegion,
  onPaymentSuccess
}: PaymentMethodsProps) {
  const regionData = getRegionData(selectedRegion);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | null>(null);
  const [showTimer, setShowTimer] = useState(false);
  const [copiedText, setCopiedText] = useState<string | null>(null);
  const handlePaymentMethodSelect = (methodType: string) => {
    setSelectedPaymentMethod(methodType);
    if (methodType !== 'Crypto') {
      setShowTimer(true);
    } else {
      setShowTimer(false);
    }
  };
  const handleCopyText = (text: string, identifier: string) => {
    navigator.clipboard.writeText(text);
    setCopiedText(identifier);
    setTimeout(() => setCopiedText(null), 2000);
  };
  const handleTimerComplete = () => {
    setShowTimer(false);
    setSelectedPaymentMethod(null);
  };
  const handlePaymentLinkClick = (paymentLink: string) => {
    window.open(paymentLink, '_blank', 'noopener,noreferrer');
  };
  return <section className="bg-white rounded-xl shadow-md p-6" data-id="element-84">
      <div className="mb-6" data-id="element-85">
        <h2 className="text-xl font-bold text-gray-900 mb-2" data-id="element-86">
          Subscription Pricing
        </h2>
        <div className="flex items-baseline" data-id="element-87">
          <span className="text-3xl font-bold text-green-600" data-id="element-88">
            {regionData.currency}
            {regionData.price}
          </span>
          <span className="ml-2 text-gray-600" data-id="element-89">for 30 days access</span>
        </div>
      </div>
      {showTimer && <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg" data-id="element-90">
          <PaymentTimer duration={300} onComplete={handleTimerComplete} data-id="element-91" />
          <p className="text-sm text-yellow-700 mt-2" data-id="element-92">
            Please complete your payment within the time limit. After expiry,
            you'll need to restart.
          </p>
        </div>}
      <h3 className="font-bold text-gray-800 mb-3" data-id="element-93">Payment Methods</h3>
      <div className="space-y-4" data-id="element-94">
        {regionData.paymentMethods.map((method, index) => <div key={index} data-id="element-95">
            {method.type === 'Crypto' ? <button onClick={() => handlePaymentMethodSelect('Crypto')} className={`w-full p-3 rounded-lg border text-left flex justify-between items-center ${selectedPaymentMethod === 'Crypto' ? 'bg-green-50 border-green-500' : 'border-gray-300 hover:bg-gray-50'}`} data-id="element-96">
                <span className="font-medium" data-id="element-97">Crypto Payment</span>
                <span className="text-sm text-gray-500" data-id="element-98">USDT, BTC, ETH</span>
              </button> : <button onClick={() => handlePaymentMethodSelect(method.type + index)} className={`w-full p-3 rounded-lg border text-left ${selectedPaymentMethod === method.type + index ? 'bg-green-50 border-green-500' : 'border-gray-300 hover:bg-gray-50'}`} data-id="element-99">
                <div className="flex justify-between items-center" data-id="element-100">
                  <span className="font-medium" data-id="element-101">{method.type}</span>
                  {method.details && <span className="text-sm text-gray-500" data-id="element-102">
                      {method.details}
                    </span>}
                </div>
                {selectedPaymentMethod === method.type + index && <div className="mt-3 space-y-2" data-id="element-103">
                    {method.accountNumber && <div className="flex items-center justify-between bg-gray-50 p-2 rounded" data-id="element-104">
                        <div data-id="element-105">
                          <div className="text-sm text-gray-500" data-id="element-106">
                            Account Number
                          </div>
                          <div data-id="element-107">{method.accountNumber}</div>
                        </div>
                        <button onClick={e => {
                e.stopPropagation();
                handleCopyText(method.accountNumber!, `account-${index}`);
              }} className="p-1 rounded-full hover:bg-gray-200" data-id="element-108">
                          {copiedText === `account-${index}` ? <Check className="h-5 w-5 text-green-600" data-id="element-109" /> : <Copy className="h-5 w-5 text-gray-600" data-id="element-110" />}
                        </button>
                      </div>}
                    {method.accountName && <div className="bg-gray-50 p-2 rounded" data-id="element-111">
                        <div className="text-sm text-gray-500" data-id="element-112">
                          Account Name
                        </div>
                        <div data-id="element-113">{method.accountName}</div>
                      </div>}
                    {method.email && <div className="flex items-center justify-between bg-gray-50 p-2 rounded" data-id="element-114">
                        <div data-id="element-115">
                          <div className="text-sm text-gray-500" data-id="element-116">Email</div>
                          <div data-id="element-117">{method.email}</div>
                        </div>
                        <button onClick={e => {
                e.stopPropagation();
                handleCopyText(method.email!, `email-${index}`);
              }} className="p-1 rounded-full hover:bg-gray-200" data-id="element-118">
                          {copiedText === `email-${index}` ? <Check className="h-5 w-5 text-green-600" data-id="element-119" /> : <Copy className="h-5 w-5 text-gray-600" data-id="element-120" />}
                        </button>
                      </div>}
                    {method.paymentLink && <button onClick={e => {
              e.stopPropagation();
              handlePaymentLinkClick(method.paymentLink!);
            }} className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition" data-id="element-121">
                        Contact Agent
                      </button>}
                    {!method.paymentLink && method.details !== 'Pay Now' && method.type !== 'Orange Money Agent' && <button onClick={e => {
              e.stopPropagation();
              setTimeout(onPaymentSuccess, 1000);
            }} className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition" data-id="element-122">
                          I've Made Payment
                        </button>}
                  </div>}
              </button>}
          </div>)}
      </div>
      {selectedPaymentMethod === 'Crypto' && <CryptoPayment onPaymentSuccess={onPaymentSuccess} data-id="element-123" />}
    </section>;
}